"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";
import { useTrack } from "@owlcat/app-shell";
import { claimSignedInOnce } from "@/contexts/games/analytics/mazeAnalytics";

/**
 * Emits the fleet `signed_in` event ONCE per browser session when a session
 * appears in this app (games has no login of its own — every session is an
 * accounts SSO hop, hence provider/method = 'sso'). Guarded by sessionStorage
 * keyed on the identity id so re-renders / route changes never re-emit.
 * Renders nothing; mounted once in the locale layout under <SessionProvider>.
 */
export function SignedInTracker() {
  const { data: session, status } = useSession();
  const track = useTrack();
  const user = session?.user as { id?: string; identityId?: string } | undefined;
  // A blanked native session (epoch bump) resolves 'authenticated' with id '' — not a sign-in.
  const identityId = status === "authenticated" && user?.id ? (user.identityId ?? user.id) : "";

  useEffect(() => {
    if (!identityId) return;
    const storage = typeof window === "undefined" ? null : window.sessionStorage;
    if (claimSignedInOnce(storage, identityId)) {
      track("signed_in", { provider: "sso", method: "sso" });
    }
  }, [identityId, track]);

  return null;
}
